import { closingPortalApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { openFileFromBlob } from 'utils/file';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';

export const CLOSING_PORTAL_ACTION_TYPE = createNamedActions('closingPortal', {
  GET_LOANS: 'GET_LOANS',
  GET_LOAN_SUMMARY: 'GET_LOAN_SUMMARY',
  GET_WIRE_INSTRUCTIONS: 'GET_WIRE_INSTRUCTIONS',
  GET_FEES: 'GET_FEES',
  GET_FEE_TYPES: 'GET_FEE_TYPES',
  GET_FEE_RECOMMENDATIONS: 'GET_FEE_RECOMMENDATIONS',
  GET_DOCUMENTS: 'GET_DOCUMENTS',
  GET_DRAFT_FEE_RECOMMENDATIONS: 'GET_DRAFT_FEE_RECOMMENDATIONS',
  RESET: 'RESET',
  RESET_LOAN_VIEW: 'RESET_LOAN_VIEW',
});

export const getClosingPortalLoansAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await closingPortalApi.getLoans();

  return dispatch({
    type: CLOSING_PORTAL_ACTION_TYPE.GET_LOANS,
    payload: data,
  });
});

export const getClosingPortalLoanSummaryAction = catchAsyncActionError(
  ({ applicationId }) =>
    async dispatch => {
      const { data } = await closingPortalApi.getLoanSummary({ applicationId });

      return dispatch({
        type: CLOSING_PORTAL_ACTION_TYPE.GET_LOAN_SUMMARY,
        payload: data,
      });
    },
);

export const getClosingPortalWireInstructionsAction = catchAsyncActionError(
  ({ loanId }) =>
    async dispatch => {
      const { data } = await closingPortalApi.getWireInstructions({ loanId });

      return dispatch({
        type: CLOSING_PORTAL_ACTION_TYPE.GET_WIRE_INSTRUCTIONS,
        payload: data,
      });
    },
);

export const confirmClosingPortalWireInstructionsAction = catchAsyncActionError(
  ({ loanId }) =>
    async dispatch => {
      await closingPortalApi.confirmWireInstructions({ loanId });

      return dispatch(getClosingPortalWireInstructionsAction({ loanId }));
    },
);

export const rejectClosingPortalWireInstructionsAction = catchAsyncActionError(
  ({ loanId, reason }) =>
    async dispatch => {
      await closingPortalApi.rejectWireInstructions({ loanId, reason });

      return dispatch(getClosingPortalWireInstructionsAction({ loanId }));
    },
);

export const getClosingPortalFeesAction = catchAsyncActionError(({ loanId }) => async dispatch => {
  const { data } = await closingPortalApi.getFees({ loanId });

  return dispatch({
    type: CLOSING_PORTAL_ACTION_TYPE.GET_FEES,
    payload: data,
  });
});

export const getClosingPortalFeeTypesAction = catchAsyncActionError(
  ({ loanId }) =>
    async dispatch => {
      const { data } = await closingPortalApi.getFeeTypes({ loanId });

      return dispatch({
        type: CLOSING_PORTAL_ACTION_TYPE.GET_FEE_TYPES,
        payload: data,
      });
    },
);

export const getClosingPortalFeeRecommendationsAction = catchAsyncActionError(
  ({ loanId }) =>
    async dispatch => {
      const { data } = await closingPortalApi.getFeeRecommendations({ loanId });

      return dispatch({
        type: CLOSING_PORTAL_ACTION_TYPE.GET_FEE_RECOMMENDATIONS,
        payload: data,
      });
    },
);

// The AI-drafted recommendations awaiting the agent's confirmation; the API answers 204 (no body) when
// there is none.
export const getClosingPortalDraftFeeRecommendationsAction = catchAsyncActionError(
  ({ loanId }) =>
    async dispatch => {
      const { data } = await closingPortalApi.getDraftFeeRecommendations({ loanId });

      return dispatch({
        type: CLOSING_PORTAL_ACTION_TYPE.GET_DRAFT_FEE_RECOMMENDATIONS,
        payload: data || null,
      });
    },
);

export const discardClosingPortalDraftFeeRecommendationsAction = catchAsyncActionError(
  ({ loanId, sessionId }) =>
    async dispatch => {
      await closingPortalApi.discardDraftFeeRecommendations({ loanId, sessionId });

      return dispatch({
        type: CLOSING_PORTAL_ACTION_TYPE.GET_DRAFT_FEE_RECOMMENDATIONS,
        payload: null,
      });
    },
);

export const submitClosingPortalFeeRecommendationsAction = catchAsyncActionError(
  ({ loanId, items, draftSessionId = null }) =>
    async dispatch => {
      await closingPortalApi.submitFeeRecommendations({ loanId, items, draftSessionId });

      if (draftSessionId) {
        dispatch({ type: CLOSING_PORTAL_ACTION_TYPE.GET_DRAFT_FEE_RECOMMENDATIONS, payload: null });
      }

      dispatch(
        showMessageAction({
          message: 'Fee recommendations submitted for lender review',
          variant: 'success',
        }),
      );

      // The POST already succeeded; a failed refresh of the submitted list must not fail the submit.
      return dispatch(getClosingPortalFeeRecommendationsAction({ loanId, silent: true })).catch(
        Error,
      );
    },
);

export const getClosingPortalDocumentsAction = catchAsyncActionError(
  ({ loanId }) =>
    async dispatch => {
      const { data } = await closingPortalApi.getDocuments({ loanId });

      // Tagged with its loan: a fetch that lands after the view moved on must not show under the next loan.
      return dispatch({
        type: CLOSING_PORTAL_ACTION_TYPE.GET_DOCUMENTS,
        payload: { loanId, items: data },
      });
    },
);

export const uploadClosingPortalDocumentAction = catchAsyncActionError(
  ({ loanId, kind, file }) =>
    async dispatch => {
      await closingPortalApi.uploadDocument({ loanId, kind, file });

      dispatch(
        showMessageAction({
          message: `${file.name} uploaded`,
          variant: 'success',
        }),
      );

      // The upload already succeeded; a failed refresh of the list must not fail the upload.
      return dispatch(getClosingPortalDocumentsAction({ loanId, silent: true })).catch(Error);
    },
);

export const openClosingPortalDocumentAction = catchAsyncActionError(
  ({ loanId, documentId, fileName }) =>
    async () => {
      const { data } = await closingPortalApi.getDocumentContent({ loanId, documentId });

      return openFileFromBlob(data, fileName);
    },
);

export const resetClosingPortalAction = () => dispatch =>
  dispatch({ type: CLOSING_PORTAL_ACTION_TYPE.RESET });

export const resetClosingPortalLoanViewAction = () => dispatch =>
  dispatch({ type: CLOSING_PORTAL_ACTION_TYPE.RESET_LOAN_VIEW });
